"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type OrkestriaTheme = "light" | "dark" | "night";

const STORAGE_KEY = "orkestria-theme";
const VALID: OrkestriaTheme[] = ["light", "dark", "night"];

type ThemeContextValue = {
  theme: OrkestriaTheme;
  setTheme: (t: OrkestriaTheme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyThemeClass(theme: OrkestriaTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("dark", "night");
  if (theme === "dark") root.classList.add("dark");
  else if (theme === "night") root.classList.add("night");
  root.setAttribute("data-theme", theme);
}

export function readStoredTheme(): OrkestriaTheme {
  if (typeof window === "undefined") return "light";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw && (VALID as string[]).includes(raw)) return raw as OrkestriaTheme;
  return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<OrkestriaTheme>("light");

  useEffect(() => {
    const initial = readStoredTheme();
    setThemeState(initial);
    applyThemeClass(initial);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = (e.newValue && (VALID as string[]).includes(e.newValue)
        ? e.newValue
        : "light") as OrkestriaTheme;
      setThemeState(next);
      applyThemeClass(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = useCallback((t: OrkestriaTheme) => {
    setThemeState(t);
    applyThemeClass(t);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, t);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useOrkestriaTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: "light",
      setTheme: () => {},
    };
  }
  return ctx;
}
